import React, { useEffect } from 'react';
import { PieChart, DollarSign, Percent, Coins, Sparkles, BarChart2, Rocket } from 'lucide-react';
import { languageService } from '../../js/languageService';

const TokenomicsSection = () => {
  const tokenDistribution = [
    { name: "Presale", percentage: 40, color: "#3B82F6" },
    { name: "Liquidity", percentage: 30, color: "#8B5CF6" },
    { name: "Team", percentage: 15, color: "#F43F5E" },
    { name: "Marketing", percentage: 10, color: "#F59E0B" },
    { name: "Ecosystem", percentage: 3, color: "#10B981" },
    { name: "Reserves", percentage: 2, color: "#6366F1" }
  ];

  const tokenInfo = [
    { title: "Token Name", value: "QuokkaX", icon: <Sparkles className="w-6 h-6" /> },
    { title: "Token Symbol", value: "QOKX", icon: <Coins className="w-6 h-6" /> },
    { title: "Total Supply", value: "1,000,000,000", icon: <BarChart2 className="w-6 h-6" /> },
    { title: "Initial Price", value: "$0.004", icon: <DollarSign className="w-6 h-6" /> },
    { title: "Blockchain", value: "Ethereum & BNB Chain", icon: <PieChart className="w-6 h-6" /> },
    { title: "Tax", value: "2% Buy / 2% Sell", icon: <Percent className="w-6 h-6" /> }
  ];

  useEffect(() => {
    const handleLanguageChange = () => {
      languageService.updatePageContent();
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, []);

  return (
    <section id="tokenomics" className="py-8 relative">
      {/* Decorative elements - matching PresaleInfo */}
      <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-b from-blue-950/2 to-transparent pointer-events-none"></div>
      <div className="absolute -bottom-20 right-0 w-80 h-80 bg-purple-900/5 rounded-full blur-[100px]"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-4">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4 gradient-heading" data-i18n="tokenomics.title">Token Economics</h2>
          <p className="text-lg sm:text-xl card-text max-w-3xl mx-auto" data-i18n="tokenomics.description">
            Dive deeper into the QuokkaX token structure and explore the economic model behind your investment
          </p>          
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 md:gap-10">
          {/* Left Column - Token Distribution */}
          <div className="lg:col-span-7">
            <div className="glass-card p-4 sm:p-6 md:p-8 hover-glow border border-blue-500/10 h-full">
              {/* Token Distribution */}
              <div className="card-content">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <h3 className="card-title flex items-center">
                    <Rocket className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 mr-2 text-primary" />
                    <span className="gradient-heading text-lg sm:text-xl md:text-2xl" data-i18n="tokenomics.distribution">Token Distribution</span>
                  </h3>
                  <div className="tokenomics-total-badge card-text">
                    <span data-i18n="tokenomics.total">Total</span>: <span className="text-primary">1,000,000,000 QOKX</span>
                  </div>
                </div>
                
                <div className="space-y-4 sm:space-y-6">
                  {/* Distribution with progress bars */}
                  <div className="grid grid-cols-1 gap-3 sm:gap-4">
                    {tokenDistribution.map((item, index) => (
                      <div key={index} className="tokenomics-distribution-item">
                        <div className="tokenomics-distribution-header">
                          <div className="tokenomics-distribution-label">
                            <div className="tokenomics-distribution-color" style={{ backgroundColor: item.color }}></div>
                            <span className="tokenomics-distribution-name card-subtitle text-base sm:text-lg" data-i18n={`tokenomics.${item.name.toLowerCase()}`}>{item.name}</span>
                          </div>
                          <span className="tokenomics-distribution-percentage card-text text-base sm:text-lg font-semibold">{item.percentage}%</span>
                        </div>
                        <div className="tokenomics-progress-bar">
                          <div 
                            className="tokenomics-progress-fill"
                            style={{ width: `${item.percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Right Column */}
          <div className="lg:col-span-5">
            <div className="flex flex-col space-y-3 sm:space-y-4">
              {/* Basic Token Details Card */}
              <div className="glass-card p-4 sm:p-6 md:p-8 hover-glow border border-blue-500/10">
                <h3 className="gradient-heading inline-block mb-3 sm:mb-4 border-b-2 border-primary/30 text-lg sm:text-xl" data-i18n="tokenomics.details">
                  Token Details
                </h3>
                
                <div className="token-details-grid">
                  {tokenInfo.map((info, index) => (
                    <div key={index} className="token-detail-card">
                      <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                        <div className="token-detail-icon">
                          {info.icon}
                        </div>
                        <h4 className="token-detail-title text-base sm:text-lg" data-i18n={`tokenomics.${info.title.toLowerCase().replace(/\s+/g, '_')}`}>{info.title}</h4>
                      </div>
                      <p className="token-detail-value text-base sm:text-lg">{info.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TokenomicsSection; 